import { useEffect, useState } from 'react';
import HackleManager from 'utils/hackle';

import { Option } from '../../../types/common.types';
import { OrderFilterInput } from '../graphQL/types';
import { TermWithType } from './useSaleListHooks';

interface Props {
  orderFilterInput: OrderFilterInput;
  handleSetFilterValue: (key: string, value: any) => void;
  handleSetOrderFilterInput: (key: string, value: any) => void;
  termWithType: TermWithType;
  selectedOrderId: string | null;
  selectedScheduleIds: string | null;
}

function useKeywordSearch({
  orderFilterInput,
  handleSetFilterValue,
  handleSetOrderFilterInput,
  termWithType,
  selectedOrderId,
  selectedScheduleIds,
}: Props) {
  const [searchText, setSearchText] = useState<string>('');
  const [searchedText, setSearchedText] = useState<string>(''); // 검색했던 값을 저장하는 state
  const [selectedKeyword, setSelectedKeyword] = useState<Option>({
    label: '전화번호',
    value: 'phoneLike',
  });

  const defaultFilter = {
    ...orderFilterInput.filter,
    productTitleLike: null,
    productIds: null,
    phoneLike: null,
    nameLike: null,
    orderIds: null,
    scheduleIds: null,
    itemNameLike: null,
  };

  // 검색 키워드 선택
  const handleSearchKeyword = (opt: Option) => {
    if (opt) {
      setSelectedKeyword({
        value: opt.value,
        label: opt.label,
      });

      handleSetFilterValue('searchType', opt.value);
      handleSetFilterValue('searchValue', searchText);
    }
  };

  const initSearchQuery = () => {
    handleSetOrderFilterInput('filter', {
      ...defaultFilter,
    });
  };

  const isAbledToSearch = !searchedText || searchText !== searchedText;

  const resetSearchQuery = () => {
    if (!isAbledToSearch) {
      initSearchQuery();
      initSearchText();

      return;
    }
  };

  // 검색 키워드 쿼리 업데이트
  const handleUpdateSearchQuery = () => {
    HackleManager.logSalesSearchComplete(
      selectedKeyword.label,
      selectedKeyword.value,
      '전체',
      // formatSalesTermType(filterValue.termType),
    );

    const getSearchValue = () => {
      if (selectedKeyword.value === 'productIds') {
        return searchText.includes(',') ? searchText.split(',') : [searchText];
      }

      return searchText.length === 0 ? null : searchText;
    };

    setSearchedText(searchText);
    handleSetOrderFilterInput('filter', {
      ...defaultFilter,
      [selectedKeyword.value]: getSearchValue(),
      transactionTerm: termWithType,
    });
  };

  // 검색 input, 검색 이력 초기화
  const initSearchText = () => {
    setSearchText(''); // 현재 input에 적혀있는 값
    setSearchedText(''); // 이전에 검색했던 값
  };

  // 검색 키워드 입력 핸들러
  const handleSearchText = (value: string) => {
    if (
      ['productIds', 'orderIds', 'scheduleIds', 'phoneLike'].includes(
        selectedKeyword.value,
      )
    ) {
      // productIds일때, 숫자와 쉼표만 허용
      const numericValue = value.replace(/[^0-9,]/g, '');
      setSearchText(numericValue);
    } else {
      setSearchText(value);
    }
  };

  useEffect(() => {
    if (selectedOrderId) {
      setSearchText(selectedOrderId);
      setSearchedText(selectedOrderId);
      handleSearchKeyword({
        label: '주문ID',
        value: 'orderIds',
      });
    }

    if (selectedScheduleIds) {
      setSearchText(selectedScheduleIds);
      setSearchedText(selectedScheduleIds);
      handleSearchKeyword({
        label: '일정ID',
        value: 'scheduleIds',
      });
    }
  }, [selectedOrderId, selectedScheduleIds]);

  useEffect(() => {
    if (selectedKeyword.value === 'productIds') {
      initSearchText();
    }
  }, [selectedKeyword.value]);

  return {
    // 상태
    selectedKeyword,
    searchText,
    isAbledToSearch,
    // 핸들러
    handleUpdateSearchQuery,
    handleSearchKeyword,
    handleSearchText,
    resetSearchQuery,
    // 초기화
    initSearchQuery,
    initSearchText,
  };
}

export default useKeywordSearch;
